import type { NodeSn } from '../../../nodes';
import {
  type KeyOfType,
  getValueFromKeyOfType,
} from '../../../types/KeyOfType';
import { createDefaultFilter } from '../../filters/createDefaultFilter';
import type { TableColumnBase } from '../TableColumnBase';
import {
  type TableDataColumnConfig,
  pickTableColumnConfig,
} from '../TableColumnConfig';
import { formatValueToCsvString } from './helpers/formatValueToCsvString';

export type TableColumnFormatHostnameObject<TRow, TObject> = {
  type: 'hostnameObject';
  field: KeyOfType<TRow, TObject | TObject[] | null>;
  hostnameProp: KeyOfType<TObject, string>;
  snProp: KeyOfType<TObject, NodeSn>;
};

export const createHostnameObjectColumn: {
  <TRow, TObject>(
    config: TableDataColumnConfig<TRow, TObject> & {
      array?: false;
      hostnameProp: KeyOfType<TObject, string>;
      snProp: KeyOfType<TObject, NodeSn>;
    },
  ): TableColumnBase<TRow, TableColumnFormatHostnameObject<TRow, TObject>>;
  <TRow, TObject>(
    config: TableDataColumnConfig<TRow, TObject[]> & {
      array: true;
      hostnameProp: KeyOfType<TObject, string>;
      snProp: KeyOfType<TObject, NodeSn>;
    },
  ): TableColumnBase<TRow, TableColumnFormatHostnameObject<TRow, TObject>>;
} = <TRow, TObject>(
  config: TableDataColumnConfig<TRow, TObject | TObject[] | null> & {
    hostnameProp: KeyOfType<TObject, string>;
    snProp: KeyOfType<TObject, NodeSn>;
  },
): TableColumnBase<TRow, TableColumnFormatHostnameObject<TRow, TObject>> => ({
  ...pickTableColumnConfig(config),
  format: {
    type: 'hostnameObject',
    field: config.key,
    hostnameProp: config.hostnameProp,
    snProp: config.snProp,
  },
  csvStringDependencies: [config.key],
  webDependencies: [config.key],
  toCsvString: (row) => {
    const value = getValueFromKeyOfType<TRow, TObject | TObject[] | null>(
      row,
      config.key,
    );

    return value
      ? formatValueToCsvString(
          value,
          (v: TObject): string =>
            getValueFromKeyOfType(v, config.hostnameProp) ?? '',
        )
      : '';
  },
  filter: createDefaultFilter(config.filter, undefined),
});
